import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { SeasonCountdown, Countdown } from "../components/SeasonCountdown";
import { Button } from "@/components/ui/button";
import { Trophy, Target, TrendingUp, ArrowRight, ListOrdered, Users, MessageSquare, Share2, CalendarDays } from "lucide-react";
import { LanguageSelect } from "@/components/LanguageSelect";

const LEAGUES = ["English Premier League", "La Liga", "Serie A", "Champions League", "Europa League", "Eredivisie", "Primeira Liga", "Saudi Pro League", "Super League Greece"];

// Featured leagues rotate by what's actually in season (month-aware), so the
// homepage always leads with live football. Priority breaks ties.
const LEAGUE_POOL = [
  { name: "Premier League", slug: "premier-league-predictor", img: "/og/premier-league.jpg", priority: 1, months: [7, 8, 9, 10, 11, 0, 1, 2, 3, 4] },
  { name: "La Liga", slug: "la-liga-predictor", img: "/og/la-liga.jpg", priority: 2, months: [7, 8, 9, 10, 11, 0, 1, 2, 3, 4] },
  { name: "Champions League", slug: "champions-league-predictor", img: "/og/champions-league.jpg", priority: 3, months: [8, 9, 10, 11, 0, 1, 2, 3, 4] },
  { name: "Serie A", slug: "serie-a-predictor", img: "/og/serie-a.jpg", priority: 4, months: [7, 8, 9, 10, 11, 0, 1, 2, 3, 4] },
  { name: "Bundesliga", slug: "bundesliga-predictor", img: "/og/bundesliga.jpg", priority: 5, months: [7, 8, 9, 10, 11, 0, 1, 2, 3, 4] },
  { name: "Ligue 1", slug: "ligue-1-predictor", img: "/og/ligue-1.jpg", priority: 6, months: [7, 8, 9, 10, 11, 0, 1, 2, 3, 4] },
  { name: "Europa League", slug: "europa-league-predictor", img: "/og/europa-league.jpg", priority: 7, months: [8, 9, 10, 11, 0, 1, 2, 3, 4] },
  { name: "Eredivisie", slug: "eredivisie-predictor", img: "/og/eredivisie.jpg", priority: 8, months: [7, 8, 9, 10, 11, 0, 1, 2, 3, 4] },
  { name: "Saudi Pro League", slug: "saudi-pro-league-predictor", img: "/og/saudi-pro-league.jpg", priority: 9, months: [7, 8, 9, 10, 11, 0, 1, 2, 3, 4] },
];

function pickFeaturedLeagues() {
  const m = new Date().getMonth();
  const byPriority = (a, b) => a.priority - b.priority;
  const featured = LEAGUE_POOL.filter((l) => l.months.includes(m)).sort(byPriority).slice(0, 4);
  for (const l of [...LEAGUE_POOL].sort(byPriority)) {
    if (featured.length >= 4) break;
    if (!featured.includes(l)) featured.push(l);
  }
  const slugs = new Set(featured.map((l) => l.slug));
  const more = LEAGUE_POOL.filter((l) => !slugs.has(l.slug)).sort(byPriority);
  return { featured, more };
}

// Edit this to your real opening-weekend kickoff. When it passes, the banner
// automatically flips to a "season is live" message.


const DERBY_DISPLAY = {
  "el-clasico": { name: "El Clásico", home: "Real Madrid", away: "Barcelona" },
  "manchester-derby": { name: "Manchester Derby", home: "Man United", away: "Man City" },
  "north-london-derby": { name: "North London Derby", home: "Arsenal", away: "Tottenham" },
  "merseyside-derby": { name: "Merseyside Derby", home: "Liverpool", away: "Everton" },
  "milan-derby": { name: "Derby della Madonnina", home: "Inter", away: "AC Milan" },
  "derby-d-italia": { name: "Derby d'Italia", home: "Juventus", away: "Inter" },
  "rome-derby": { name: "Derby della Capitale", home: "Roma", away: "Lazio" },
  "der-klassiker": { name: "Der Klassiker", home: "Bayern", away: "Dortmund" },
  "revierderby": { name: "Revierderby", home: "Dortmund", away: "Schalke" },
  "le-classique": { name: "Le Classique", home: "PSG", away: "Marseille" },
  "de-klassieker": { name: "De Klassieker", home: "Ajax", away: "Feyenoord" },
  "madrid-derby": { name: "Madrid Derby", home: "Atlético", away: "Real Madrid" },
  "seville-derby": { name: "Seville Derby", home: "Real Betis", away: "Sevilla" },
  "old-firm": { name: "The Old Firm", home: "Celtic", away: "Rangers" },
  "superclasico": { name: "El Superclásico", home: "Boca Juniors", away: "River Plate" },
  "istanbul-derby": { name: "Intercontinental Derby", home: "Galatasaray", away: "Fenerbahçe" },
};
const SPOTLIGHT_FALLBACK = ["el-clasico", "manchester-derby", "milan-derby", "der-klassiker", "north-london-derby", "le-classique", "de-klassieker", "old-firm"];

function DerbySpotlight() {
  const [derbies, setDerbies] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/derbies`)
      .then((r) => r.json())
      .then((d) => setDerbies(d.derbies || []))
      .catch(() => setDerbies([]));
  }, []);
  let pickSlug = null, pickDate = null;
  if (derbies && derbies.length) {
    const dated = derbies.filter((x) => x.next_date).map((x) => ({ slug: x.slug, date: new Date(x.next_date) })).sort((a, b) => a.date - b.date);
    if (dated.length) { pickSlug = dated[0].slug; pickDate = dated[0].date; }
  }
  if (!pickSlug) {
    const wk = Math.floor(Date.now() / (7 * 24 * 3600 * 1000));
    pickSlug = SPOTLIGHT_FALLBACK[wk % SPOTLIGHT_FALLBACK.length];
  }
  const d = DERBY_DISPLAY[pickSlug];
  if (!d) return null;
  return (
    <section data-testid="derby-spotlight" className="border-b-2 border-border">
      <div className="grid md:grid-cols-2">
        <Link to={`/predictor/${pickSlug}`} data-testid="spotlight-image" className="group relative aspect-[3/2] overflow-hidden border-b-2 md:border-b-0 md:border-e-2 border-border">
          <img src={`/og/derby-${pickSlug}.jpg`} alt={`${d.name} predictor`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        </Link>
        <div className="flex flex-col items-start justify-center gap-4 p-6 md:p-10">
          <p className="overline text-primary">{pickDate ? "The next big one" : "Derby of the week"}</p>
          <h2 className="font-display text-3xl md:text-5xl uppercase leading-none" data-testid="spotlight-name">{d.name}</h2>
          <p className="font-data text-sm uppercase tracking-[0.15em] text-muted-foreground">{d.home} <span className="text-primary">vs</span> {d.away}</p>
          {pickDate && <Countdown target={pickDate} label="Kicks off in" testid="spotlight-countdown" />}
          <Link to={`/predictor/${pickSlug}`}><Button className="rounded-none bg-primary text-primary-foreground uppercase font-bold" data-testid="spotlight-cta">Predict the derby <ArrowRight size={15} className="ms-1" /></Button></Link>
        </div>
      </div>
    </section>
  );
}

function LeagueRotator() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % LEAGUES.length), 2000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="hidden sm:inline-flex items-center font-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground" data-testid="league-rotator">
      <span className="mx-2 text-border">/</span>
      <AnimatePresence mode="wait">
        <motion.span
          key={LEAGUES[i]}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="text-primary whitespace-nowrap"
        >
          {LEAGUES[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

const HERO_IMG =
  "https://images.unsplash.com/photo-1665413811870-5b29a250f64a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900&h=1200&fit=crop&ixlib=rb-4.1.0";

const STEPS = [
  { num: "01", icon: ListOrdered, title: "Pick your league", desc: "Choose from 10 top-flight leagues across Europe. Start with the one you know inside out." },
  { num: "02", icon: Target, title: "Forecast the season", desc: "Lock in your final table, then call every weekend's scoreline before a ball is kicked." },
  { num: "03", icon: TrendingUp, title: "Climb the ranks", desc: "Watch your predictions battle the real results and rise up the fan leaderboard." },
];

const HOOKS = [
  { icon: Trophy, label: "10 leagues", sub: "Europe's biggest divisions" },
  { icon: CalendarDays, label: "Week by week", sub: "Edit as form changes" },
  { icon: Users, label: "Private leagues", sub: "Battle your mates" },
  { icon: MessageSquare, label: "Banter boards", sub: "Talk your team up" },
  { icon: Share2, label: "Shareable card", sub: "Post your table" },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export default function Landing() {
  const { featured, more } = pickFeaturedLeagues();
  return (
    <div className="relative min-h-screen bg-background overflow-hidden grain">
      <Helmet>
        <title>GAFFER — Free Football Score Predictor | Predict &amp; Beat Your Mates</title>
        <meta name="description" content="GAFFER is a free football score predictor — predict Premier League, La Liga, Serie A, Bundesliga, Ligue 1, Champions League &amp; more, week by week, and watch your table take shape. Beat your mates in private banter leagues." />
        <link rel="canonical" href="https://thefootballgaffer.com/" />
      </Helmet>
      {/* Masthead */}
      <nav className="relative z-10 flex items-center justify-between px-4 md:px-10 py-4 border-b-2 border-foreground">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-3xl md:text-4xl tracking-tight leading-none">GAFFER<span className="text-primary">.</span></span>
          <span className="hidden sm:inline font-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Matchday No. 01</span>
          <LeagueRotator />
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <LanguageSelect />
          <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login-btn">Log in</Button></Link>
          <Link to="/register"><Button size="sm" data-testid="nav-register-btn">Get started</Button></Link>
        </div>
      </nav>

      {/* Ticker strip */}
      <div className="border-b-2 border-border bg-primary text-primary-foreground overflow-hidden whitespace-nowrap">
        <p className="font-data text-xs uppercase tracking-[0.2em] py-1.5 px-4 truncate">
          ⚽ 10 leagues · predict every scoreline · call the final table · win the banter ⚽ predict · track · argue
        </p>
      </div>

      <main className="relative z-10 px-4 md:px-10">
        {/* Hero: asymmetric split */}
        <section className="grid lg:grid-cols-12 gap-0 border-b-2 border-border">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-8 py-12 md:py-20 lg:pe-10 flex flex-col justify-center"
          >
            <SeasonCountdown className="self-start mb-5" />
            <p className="overline text-primary mb-4">Predict · Track · Win the banter</p>
            <h1 className="font-display uppercase text-[17vw] sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.84] tracking-tight">
              Call it.<br />Own it.
            </h1>
            <p className="text-muted-foreground text-base md:text-lg mt-6 max-w-xl leading-relaxed">
              Predict the final league table and every scoreline, then watch your forecast battle the
              real results week by week. No guesswork — just proper football knowledge.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/register">
                <Button size="lg" data-testid="hero-cta-btn" className="text-base hover:-translate-y-0.5 hard-shadow-primary">
                  Start predicting <ArrowRight className="ms-2" size={18} />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" data-testid="hero-login-btn" className="text-base">
                  I have an account
                </Button>
              </Link>
            </div>
            <p className="font-data text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-6">
              Free to play · No card needed · Season 2026/27
            </p>
          </motion.div>
          <div className="lg:col-span-4 relative min-h-[300px] border-t-2 lg:border-t-0 lg:border-s-2 border-border">
            <img src={HERO_IMG} alt="Football fans packed on the terraces" className="absolute inset-0 w-full h-full object-cover zine-img" />
            <div className="absolute inset-0 bg-primary/25 mix-blend-multiply" />
            <div className="absolute bottom-4 start-4 bg-background border-2 border-foreground px-3 py-1.5">
              <span className="font-data text-[10px] uppercase tracking-[0.2em]">The 3pm feeling</span>
            </div>
          </div>
        </section>

        {/* Featured leagues — flagship competitions front and centre */}
        <section data-testid="featured-leagues-section" className="border-b-2 border-border">
          <div className="flex items-end justify-between gap-4 px-1 py-8 md:py-10">
            <div>
              <p className="overline text-primary mb-2">Pick your competition</p>
              <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-none">Predict your league</h2>
            </div>
            <Link to="/how-it-works" data-testid="featured-all-leagues" className="hidden md:inline font-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground hover:text-primary transition-colors">All leagues →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 border-t-2 border-border">
            {featured.map((l) => (
              <Link
                key={l.slug}
                to={`/predictor/${l.slug}`}
                data-testid={`featured-league-${l.slug}`}
                className="group relative aspect-[3/2] overflow-hidden border-border border-e-2 [&:nth-child(2n)]:border-e-0 lg:[&:nth-child(2n)]:border-e-2 lg:last:border-e-0 border-b-2 lg:border-b-0"
              >
                <img src={l.img} alt={`${l.name} score predictor`} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/10 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 flex items-center justify-between px-4 py-3">
                  <span className="font-display text-lg md:text-2xl uppercase leading-none">{l.name}</span>
                  <ArrowRight size={16} className="shrink-0 text-primary translate-x-0 opacity-70 group-hover:translate-x-1 group-hover:opacity-100 transition-all" />
                </div>
                <span className="absolute inset-0 ring-inset ring-primary opacity-0 group-hover:opacity-100 group-hover:ring-2 transition-opacity" aria-hidden="true" />
              </Link>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-4 border-t-2 border-border">
            <span className="font-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Also on GAFFER</span>
            {more.map((l) => (
              <Link key={l.slug} to={`/predictor/${l.slug}`} data-testid={`more-league-${l.slug}`}
                className="font-data text-[10px] uppercase tracking-[0.2em] text-foreground/75 hover:text-primary transition-colors">{l.name}</Link>
            ))}
          </div>
        </section>

        <DerbySpotlight />

        {/* How it works */}
        <section data-testid="how-it-works-section" className="border-b-2 border-border">
          <div className="flex items-end justify-between gap-4 px-1 py-8 md:py-10">
            <div>
              <p className="overline text-primary mb-2">How it works</p>
              <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-none">Three steps to glory</h2>
            </div>
            <span className="hidden md:inline font-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Sub-60 seconds to start</span>
          </div>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 border-t-2 border-border"
          >
            {STEPS.map((s) => (
              <motion.div
                key={s.num}
                variants={item}
                data-testid={`step-${s.num}`}
                className="group relative overflow-hidden p-6 md:p-8 border-border md:border-e-2 last:border-e-0 border-t-2 md:border-t-0 hover:bg-primary hover:text-primary-foreground transition-colors duration-150"
              >
                <span className="pointer-events-none absolute -top-6 -end-2 font-display text-[7rem] leading-none text-border/40 group-hover:text-primary-foreground/20 transition-colors select-none">
                  {s.num}
                </span>
                <s.icon size={28} className="relative text-primary group-hover:text-primary-foreground transition-colors" />
                <h3 className="relative font-display text-2xl md:text-3xl uppercase mt-6">{s.title}</h3>
                <p className="relative text-muted-foreground text-sm mt-2 max-w-xs group-hover:text-primary-foreground/85 transition-colors">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* Hooks / value reinforcement */}
        <section className="grid grid-cols-2 md:grid-cols-5 border-b-2 border-border">
          {HOOKS.map((h, i) => (
            <div
              key={i}
              data-testid={`hook-${i}`}
              className="p-5 md:p-6 border-border border-e-2 [&:nth-child(2n)]:border-e-0 md:[&:nth-child(2n)]:border-e-2 md:last:border-e-0 border-t-2 first:border-t-2 md:border-t-0 flex flex-col gap-2"
            >
              <h.icon size={20} className="text-primary" />
              <span className="font-display text-xl md:text-2xl uppercase leading-none">{h.label}</span>
              <span className="font-data text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{h.sub}</span>
            </div>
          ))}
        </section>

        {/* Final CTA band */}
        <section className="border-b-2 border-border bg-primary text-primary-foreground">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 px-6 md:px-8 py-10 md:py-14">
            <div>
              <p className="font-data text-[10px] uppercase tracking-[0.25em] mb-3">Kick-off is coming</p>
              <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.9]">
                Reckon you can call the table?
              </h2>
            </div>
            <Link to="/register">
              <Button size="lg" data-testid="final-cta-btn"
                className="text-base bg-background text-foreground border-2 border-background hover:bg-transparent hover:text-primary-foreground hover:border-primary-foreground hard-shadow">
                Start predicting <ArrowRight className="ms-2" size={18} />
              </Button>
            </Link>
          </div>
        </section>

        <footer className="py-6 flex items-center justify-between">
          <span className="font-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">GAFFER — the fan's prediction desk</span>
          <Link to="/how-it-works" className="font-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors" data-testid="footer-how-it-works">How it works</Link>
        </footer>
      </main>
    </div>
  );
}
